import styled from "styled-components";

export const StyledAbout = styled.section`
  position: relative;
  isolation: isolate;
  background: radial-gradient(circle at 10% 20%, rgba(255, 72, 251, 0.15), transparent 55%),
    radial-gradient(circle at 80% 0%, rgba(110, 142, 255, 0.2), transparent 45%),
    ${({ theme }) => theme.backgroundColor.dark};
  color: ${({ theme }) => theme.color.lightGray};
  padding: clamp(4rem, 7vw, 6rem) clamp(1.5rem, 6vw, 5rem);
  overflow: hidden;
`;

export const AboutContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: clamp(2rem, 4vw, 4rem);
  margin-top: 4rem;
`;

export const AboutImageContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 28rem;
  border-radius: 2rem;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 35px 80px rgba(0, 0, 0, 0.45);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.02));

  &::after {
    content: "";
    position: absolute;
    inset: 1.25rem;
    border-radius: 1.5rem;
    background: radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.25), transparent 60%);
    pointer-events: none;
  }

  @media all and (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    min-height: 22rem;
  }
`;

export const AboutImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 2rem;
  position: relative;
  z-index: 1;
`;

export const AboutDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

export const AboutIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const AboutHighlights = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

export const HighlightCard = styled.div`
  padding: 1.25rem;
  border-radius: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(14px);
`;

export const HighlightTitle = styled.h3`
  margin: 0 0 0.4rem;
  font-size: 1.05rem;
  color: ${({ theme }) => theme.color.light};
`;

export const HighlightDescription = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.color.lightGray};
  line-height: 1.6;
`;

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
`;

export const StatCard = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 1rem;
  padding: 1.25rem;
  background: rgba(0, 0, 0, 0.35);
  text-align: left;
`;

export const StatValue = styled.span`
  display: block;
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.color.light};
`;

export const StatLabel = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.color.lightGray};
`;

export const ToolBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

export const ToolChip = styled.span`
  padding: 0.45rem 1.05rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.05);
  font-size: 0.85rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

export const Resume = styled.div`
  display: inline-block;
  margin-top: 3rem;
  position: relative;
  transition: 0.3s ease;

  &::after {
    content: "";
    display: block;
    position: absolute;
    right: 0;
    left: 0;
    bottom: -0.5rem;
    height: 1px;
    border-radius: 5px;
    background: ${({ theme }) => theme.backgroundColor.light};
    width: 100%;
    transition: transform 0.3s ease;
    transform: scaleX(0);
    transform-origin: bottom right;
  }

  &:hover::after {
    transform: scaleX(1);
    transform-origin: bottom left;
  }

  @media all and (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin-top: 1rem;
  }
`;

export const ResumeLink = styled.a`
  color: #fff;
  letter-spacing: 2px;
  font-weight: 600;
  cursor: pointer;

  &::before {
    content: ${(props) => `url(${props.icon})`};
    position: absolute;
    right: -2rem;
    opacity: 0;
    filter: alpha(opacity=0);
    -webkit-transform: translateX(-20px);
    transform: translateX(-20px);
    transition: all 0.3s;
  }

  &:hover::before {
    opacity: 1;
    filter: alpha(opacity=100);
    -webkit-transform: translateX(0);
    transform: translateX(0);
  }

  @media all and (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: 1rem;
  }
`;



export const SectionHeading = styled.div`
  text-align: center;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${(props) => props.mb || "3rem"};
`;

export const SectionTitle = styled.h2`
  margin-bottom: 0.5rem;
  position: relative;
  font-size: 3rem;
  font-weight: 800;
  text-transform: capitalize;
  z-index: 1;
  color: ${(props) => (props.dark ? "#f2f2f2" : "#262637")};
  display: inline-block;

  &::after {
    position: absolute;
    content: "${(props) => props.number}";
    left: 5%;
    top: 50%;
    transform: translate(-50%, -50%);
    color: ${(props) =>
      props.dark ? "rgba(29, 28, 28, 0.2)" : "rgba(0, 0, 0, 0.06)"};
    z-index: -1;
    font-size: 8rem;

    @media all and (max-width: ${({ theme }) => theme.breakpoints.lg}) {
      font-size: 6rem;
    }
  }

  @media all and (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    font-size: 2.5rem;
  }
`;